/**
 * QR Code Management Constants
 * Centralized query keys, status/type definitions, and helper constants
 */

// ============================================================================
// REACT QUERY KEYS
// ============================================================================
export const QRCODE_QUERY_KEYS = {
  all: ['qrcodes'] as const,
  listView: ['qrcodes', 'list-view'] as const,
  myListView: ['qrcodes', 'my-list-view'] as const,
  view: (id: string) => ['qrcodes', 'view', id] as const,
  analytics: (id: string) => ['qrcodes', 'analytics', id] as const,
  scans: (id: string) => ['qrcodes', 'scans', id] as const,
  stats: ['qrcodes', 'stats'] as const,
  frames: ['qrcode-frames'] as const,
} as const;

// ============================================================================
// QR CODE TYPES
// ============================================================================
export const QR_CODE_TYPES = {
  URL: 'URL',
  VCARD: 'vCard',
  WIFI: 'WiFi',
  EMAIL: 'Email',
  SMS: 'SMS',
  PHONE: 'Phone',
  TEXT: 'Text',
  EVENT: 'Event',
  LOCATION: 'Location',
  APP_STORE: 'AppStore',
} as const;

export type QrCodeType = (typeof QR_CODE_TYPES)[keyof typeof QR_CODE_TYPES];

// ============================================================================
// QR CODE STATUSES
// ============================================================================
export const QR_CODE_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  EXPIRED: 'Expired',
} as const;

export type QrCodeStatus = (typeof QR_CODE_STATUS)[keyof typeof QR_CODE_STATUS];

// ============================================================================
// STATUS COLORS (for badges)
// ============================================================================
export const STATUS_COLORS: Record<string, string> = {
  Active: 'green',
  Inactive: 'gray',
  Expired: 'red',
};

// ============================================================================
// TYPE COLORS (for badges)
// ============================================================================
export const TYPE_COLORS: Record<string, string> = {
  URL: 'blue',
  vCard: 'violet',
  WiFi: 'teal',
  Email: 'orange',
  SMS: 'cyan',
  Phone: 'lime',
  Text: 'gray',
  Event: 'pink',
  Location: 'red',
  AppStore: 'indigo',
};

// ============================================================================
// STATUS OPTIONS (for Select components)
// ============================================================================
export const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Expired', label: 'Expired' },
];

// ============================================================================
// TYPE OPTIONS (for Select components)
// ============================================================================
export const TYPE_OPTIONS = [
  { value: 'URL', label: 'URL / Website' },
  { value: 'vCard', label: 'vCard / Contact' },
  { value: 'WiFi', label: 'WiFi Network' },
  { value: 'Email', label: 'Email' },
  { value: 'SMS', label: 'SMS / Text Message' },
  { value: 'Phone', label: 'Phone Number' },
  { value: 'Text', label: 'Plain Text' },
  { value: 'Event', label: 'Calendar Event' },
  { value: 'Location', label: 'Location / GPS' },
  { value: 'AppStore', label: 'App Store Links' },
];

// ============================================================================
// WIFI ENCRYPTION OPTIONS
// ============================================================================
export const WIFI_ENCRYPTION_OPTIONS = [
  { value: 'WPA', label: 'WPA/WPA2' },
  { value: 'WEP', label: 'WEP' },
  { value: 'nopass', label: 'No Password' },
];

// ============================================================================
// ERROR CORRECTION OPTIONS
// ============================================================================
export const ERROR_CORRECTION_OPTIONS = [
  { value: 'Low', label: 'Low (7%)' },
  { value: 'Medium', label: 'Medium (15%)' },
  { value: 'High', label: 'High (25%)' },
  { value: 'Highest', label: 'Highest (30%)' },
];

// ============================================================================
// PATTERN STYLE OPTIONS
// ============================================================================
export const PATTERN_STYLE_OPTIONS = [
  { value: 'Square', label: 'Square' },
  { value: 'Dots', label: 'Dots' },
  { value: 'Rounded', label: 'Rounded' },
  { value: 'Classy', label: 'Classy' },
  { value: 'ClassyRounded', label: 'Classy Rounded' },
];

// ============================================================================
// CORNER STYLE OPTIONS
// ============================================================================
export const CORNER_STYLE_OPTIONS = [
  { value: 'Square', label: 'Square' },
  { value: 'Dot', label: 'Dot' },
  { value: 'ExtraRounded', label: 'Extra Rounded' },
];
